/**
 * SkerpaInterviewMode — Wizard for answering questions one at a time.
 * Replaces the normal chat input when active.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, Send, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SkerpaInterviewModeProps {
  questions: string[]
  onComplete: (combinedAnswer: string) => void
  onCancel: () => void
}

export function SkerpaInterviewMode({ questions, onComplete, onCancel }: SkerpaInterviewModeProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answers, setAnswers] = useState<string[]>(() => questions.map(() => ''))
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [currentIndex])

  const setAnswer = useCallback((value: string) => {
    setAnswers(prev => {
      const next = [...prev]
      next[currentIndex] = value
      return next
    })
  }, [currentIndex])

  const goNext = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1)
    }
  }, [currentIndex, questions.length])

  const goBack = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1)
    }
  }, [currentIndex])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (currentIndex < questions.length - 1) {
        goNext()
      } else {
        handleSubmit()
      }
    }
  }, [currentIndex, questions.length, goNext])

  const handleSubmit = useCallback(() => {
    const parts = questions.map((q, i) => {
      const answer = answers[i]?.trim()
      return answer ? `**${q}**\n${answer}` : null
    }).filter(Boolean)

    if (parts.length === 0) return
    onComplete(parts.join('\n\n'))
  }, [questions, answers, onComplete])

  const answeredCount = answers.filter(a => a.trim()).length
  const isLast = currentIndex === questions.length - 1

  return (
    <div className="border-t border-border-subtle bg-warm-100 flex flex-col">
      {/* Progress bar */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-medium text-text-secondary">
            Question {currentIndex + 1} of {questions.length}
          </span>
          <button
            onClick={onCancel}
            className="text-[11px] text-text-muted hover:text-text-secondary transition-colors flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Cancel
          </button>
        </div>
        {/* Progress dots */}
        <div className="flex gap-1">
          {questions.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === currentIndex
                  ? 'bg-business flex-[2]'
                  : answers[i]?.trim()
                    ? 'bg-business/40 flex-1'
                    : 'bg-warm-300 flex-1',
              )}
            />
          ))}
        </div>
      </div>

      {/* Question */}
      <div className="px-4 py-2">
        <p className="text-[13px] font-medium text-text leading-relaxed">
          {questions[currentIndex]}
        </p>
      </div>

      {/* Answer input */}
      <div className="px-4 pb-2">
        <textarea
          ref={inputRef}
          value={answers[currentIndex]}
          onChange={(e) => setAnswer(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Your answer..."
          rows={2}
          className={cn(
            'w-full resize-none text-[13px] text-text bg-white rounded-lg',
            'border border-border-subtle px-3 py-2',
            'placeholder:text-text-muted',
            'focus:outline-none focus:ring-1 focus:ring-business/30 focus:border-business/40',
            'max-h-28 overflow-y-auto',
          )}
        />
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-4 pb-3">
        <button
          onClick={goBack}
          disabled={currentIndex === 0}
          className={cn(
            'flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-lg transition-colors',
            currentIndex === 0
              ? 'text-warm-400 cursor-not-allowed'
              : 'text-text-secondary hover:bg-warm-200/60',
          )}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back
        </button>

        <span className="text-[10px] text-text-muted">
          {answeredCount}/{questions.length} answered
        </span>

        {isLast ? (
          <button
            onClick={handleSubmit}
            disabled={answeredCount === 0}
            className={cn(
              'flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-lg font-medium transition-colors',
              answeredCount === 0
                ? 'bg-warm-200 text-warm-400 cursor-not-allowed'
                : 'bg-business text-white hover:opacity-90',
            )}
          >
            <Send className="w-3.5 h-3.5" />
            Submit all
          </button>
        ) : (
          <button
            onClick={goNext}
            className="flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-lg text-business hover:bg-business/10 transition-colors"
          >
            {answers[currentIndex]?.trim() ? 'Next' : 'Skip'}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}
